import { type JSX, Show, lazy } from "solid-js";

import { MetaTitle } from "@/components/Base/MetaContainer/index.tsx";
import { RouteTransitionWrapper } from "@/components/Base/Transition/Index";
import { H5LobbyLayout } from "@/layouts/h5/LobbyLayout";
import { H5SubLayout } from "@/layouts/h5/SubLayout";
import { PCLobbyLayout } from "@/layouts/pc/pcLayouts.tsx";
import { isMobile } from "@/lib/isMobile";
import { userStore } from "@/stores/userStore.ts";
import { Meta } from "@solidjs/meta";
import { type RouteSectionProps } from "@solidjs/router";

import walletRoutes from "./wallet.tsx";

const PCPromotionDetails = lazy(
  () => import("@/pages/pc/menu/promotion/details"),
);
const H5Promotion = lazy(() => import("@/pages/h5/menu/promotion"));
const PCPromotion = lazy(() => import("@/pages/pc/menu/promotion"));
const H5PromotionDetails = lazy(
  () => import("@/pages/h5/menu/promotion/details"),
);
const CasinoPage = lazy(() => import("@/pages/casinoLayout.tsx"));
const H5Game = lazy(() => import("@/pages/h5/game"));
// const H5GameDetail = lazy(() => import("@/pages/h5/gamedetail"));
const H5GameList = lazy(() => import("@/pages/h5/gamelist"));
const H5Menu = lazy(() => import("@/pages/h5/menu"));
const H5BonusGame = lazy(() => import("@/pages/h5/menu/bonusGame"));
const H5Collections = lazy(() => import("@/pages/h5/menu/collections"));
const H5HotGame = lazy(() => import("@/pages/h5/menu/hotGame"));
const H5RecentGame = lazy(() => import("@/pages/h5/menu/recentGame"));
const H5ProfilePage = lazy(() => import("@/pages/h5/profile"));
const H5Vip = lazy(() => import("@/pages/h5/vip"));
const PCGame = lazy(() => import("@/pages/pc/game"));
// const PCGameDetail = lazy(() => import("@/pages/pc/gamedetail"));
const PCGameList = lazy(() => import("@/pages/pc/gamelist"));
const PCBonusGame = lazy(() => import("@/pages/pc/menu/bonusGame"));
const PCCollections = lazy(() => import("@/pages/pc/menu/collections"));
const PCHotGame = lazy(() => import("@/pages/pc/menu/hotGame"));
const PCRecentGame = lazy(() => import("@/pages/pc/menu/recentGame"));
const PCProfilePage = lazy(() => import("@/pages/pc/profile"));
const PCVip = lazy(() => import("@/pages/pc/vip"));
const NotFoundPage = lazy(() => import("./404.tsx"));
const ErrorPage = lazy(() => import("./error.tsx"));

export const RootLayout = (props: RouteSectionProps) => {
  if (isMobile()) {
    return <>{props.children}</>;
  } else {
    return (
      <PCLobbyLayout>
        <RouteTransitionWrapper>{props.children}</RouteTransitionWrapper>
      </PCLobbyLayout>
    );
  }
};

export const LobbyLayout = (props: RouteSectionProps) => {
  if (isMobile()) {
    return (
      <H5LobbyLayout>
        <RouteTransitionWrapper>{props.children}</RouteTransitionWrapper>
      </H5LobbyLayout>
    );
  } else {
    return props.children;
  }
};

export const DefaultLobbyLayout = (props: RouteSectionProps) => {
  if (isMobile()) {
    return (
      <H5LobbyLayout>
        <RouteTransitionWrapper>{props.children}</RouteTransitionWrapper>
      </H5LobbyLayout>
    );
  } else {
    return (
      <PCLobbyLayout>
        <RouteTransitionWrapper>{props.children}</RouteTransitionWrapper>
      </PCLobbyLayout>
    );
  }
};

export const SubLayout = (props: RouteSectionProps) => {
  if (isMobile()) {
    return (
      <RouteTransitionWrapper direction="right">
        <H5SubLayout {...props} />
      </RouteTransitionWrapper>
    );
  } else {
    return props.children;
  }
};

export const ConditionalBonusCenterLayout = (props: RouteSectionProps) => {
  if (isMobile()) {
    return (
      <Show
        when={userStore.isLogin}
        fallback={
          <RouteTransitionWrapper direction="right">
            <H5SubLayout {...props} />
          </RouteTransitionWrapper>
        }
      >
        <H5LobbyLayout>
          <RouteTransitionWrapper>{props.children}</RouteTransitionWrapper>
        </H5LobbyLayout>
      </Show>
    );
  } else {
    return props.children;
  }
};

const withMeta =
  (
    titleComponent: () => JSX.Element,
    pageComponent: () => JSX.Element,
    options?: { noindex?: boolean },
  ) =>
  () => {
    return (
      <>
        {titleComponent()}
        {/* 不需要被搜索引擎索引 不进行收录 */}
        {options?.noindex && <Meta name="robots" content="noindex, nofollow" />}
        {pageComponent()}
      </>
    );
  };

export const layoutRoutes = () => {
  const NotifyPage = lazy(() => import("../pages/pc/notify/notify.tsx"));

  const H5BonusCenter = lazy(
    () => import("@/pages/h5/bonuscenter/bonuscenter"),
  );

  const PCBonusCenter = lazy(
    () => import("@/pages/pc/bonuscenter/bonuscenter"),
  );

  const H5SettingsPage = lazy(() => import("@/pages/h5/settings"));
  const PCSettingsPage = lazy(() => import("@/pages/pc/settings"));

  const SettingsPage = isMobile() ? H5SettingsPage : PCSettingsPage;

  const ProfilePage = isMobile() ? H5ProfilePage : PCProfilePage;

  const GamePage = isMobile() ? H5Game : PCGame;
  const PersonalizedGamesPage = lazy(
    () => import("@/pages/pc/personalizedgames/index.tsx"),
  );
  const CasualGamesPage = lazy(
    () => import("@/pages/common/casualGames/index.tsx"),
  );
  const ProvidersPage = lazy(() =>
    isMobile()
      ? import("@/pages/h5/providers/index.tsx")
      : import("@/pages/providers/index.tsx"),
  );
  const NobruPage = lazy(() => import("@/pages/nobru/index.tsx"));
  const ProvidersDetailPage = lazy(
    () => import("@/pages/providers/[name].tsx"),
  );

  const BonusCenterPage = isMobile() ? H5BonusCenter : PCBonusCenter;
  const GamelistPage = isMobile() ? H5GameList : PCGameList;
  // const GamedetailPage = isMobile() ? H5GameDetail : PCGameDetail;

  const Collections = isMobile() ? H5Collections : PCCollections;

  const RecentGame = isMobile() ? H5RecentGame : PCRecentGame;

  const HotGame = isMobile() ? H5HotGame : PCHotGame;

  const BonusGame = isMobile() ? H5BonusGame : PCBonusGame;

  const VipPage = isMobile() ? H5Vip : PCVip;

  const PromotionPage = isMobile() ? H5Promotion : PCPromotion;

  const PromotionDetailsPage = isMobile()
    ? H5PromotionDetails
    : PCPromotionDetails;

  const MenuPage = H5Menu;

  const Feedback = lazy(() =>
    isMobile()
      ? import("@/pages/h5/feedback/index.tsx")
      : import("@/pages/pc/feedback/index.tsx"),
  );
  const HelpPage = lazy(() =>
    isMobile()
      ? import("@/pages/h5/help/index.tsx")
      : import("@/pages/pc/help/index.tsx"),
  );

  const AffiliatePage = lazy(() =>
    isMobile()
      ? import("@/pages/h5/affiliate")
      : import("@/pages/pc/affiliate"),
  );

  const ResponsiblePage = lazy(() =>
    isMobile()
      ? import("@/pages/h5/responsible/index.tsx")
      : import("@/pages/pc/responsible/index.tsx"),
  );

  const InstallPage = lazy(() =>
    isMobile()
      ? import("@/pages/h5/install/index.tsx")
      : import("@/pages/pc/install/index.tsx"),
  );
  const WagerContest = lazy(() =>
    isMobile()
      ? import("@/pages/h5/menu/wagerContest/index.tsx")
      : import("@/pages/pc/menu/wagerContest/index.tsx"),
  );
  const MyProfile = lazy(() => import("@/pages/h5/myProfile/index.tsx"));
  const SportsPage = lazy(() => import("@/pages/sports/[gid].tsx"));
  const SponsorshipPage = lazy(() => import("@/pages/sponsorship/index.tsx"));

  const TournamentPage = lazy(() =>
    isMobile()
      ? import("@/pages/h5/competition/index.tsx")
      : import("@/pages/pc/competition/index.tsx"),
  );

  return [
    {
      path: "/*",
      component: RootLayout,
      children: [
        {
          path: "/",
          component: LobbyLayout,
          children: [
            {
              path: "/",
              // component: withMeta(MetaTitle.Home, IndexPage),
              component: withMeta(MetaTitle.Home, CasinoPage),
            },
            ...(isMobile()
              ? [
                  {
                    path: "menu",
                    component: withMeta(MetaTitle.Menu, MenuPage, {
                      noindex: !isMobile(),
                    }),
                  },
                ]
              : []),
            // {
            //   path: "casino",
            //   component: withMeta(MetaTitle.Casino, CasinoPage),
            // },
            {
              path: "profile",
              component: withMeta(MetaTitle.MyProfile, ProfilePage, {
                noindex: true,
              }),
            },
            {
              path: "affiliate/*",
              component: withMeta(MetaTitle.Affiliate, AffiliatePage),
            },
            {
              path: "install",
              component: withMeta(MetaTitle.Install, InstallPage),
            },
            {
              path: "sports/:gid",
              component: withMeta(MetaTitle.Home, SportsPage),
            },
            {
              path: "casual-games",
              component: withMeta(MetaTitle.CasualGames, CasualGamesPage),
              info: {
                needLogin: true,
              },
            },
            {
              path: "promotion",
              component: withMeta(MetaTitle.Promotion, PromotionPage),
            },
            import.meta.env.VITE_APP_NAME === "br" && {
              path: "sponsorship",
              component: withMeta(MetaTitle.Sponsorship, SponsorshipPage),
            },
            {
              path: "nobru",
              component: NobruPage,
            },
          ].filter((route) => !!route),
        },
        {
          path: "/*",
          component: SubLayout,
          children: [
            // 非菜单栏进入的页面(modal route)
            {
              path: "game/:gid",
              component: GamePage,
              info: { needLogin: true, needKYC: true },
            },

            {
              path: "tournament",
              component: withMeta(MetaTitle.Tournament, TournamentPage),
            },
            {
              path: "personalized-game",
              // prettier-ignore
              component: withMeta(MetaTitle.PersonalizedGames,PersonalizedGamesPage),
              info: { needLogin: true },
            },
            {
              path: "providers",
              component: withMeta(MetaTitle.Providers, ProvidersPage),
            },
            {
              path: "providers/:name",
              component: withMeta(MetaTitle.Providers, ProvidersDetailPage),
            },
            {
              path: "game-list/:type",
              component: GamelistPage,
            },

            // 菜单栏 (modal route)
            {
              path: "hot-game",
              component: withMeta(MetaTitle.HotGames, HotGame),
            },
            {
              path: "collections",
              component: withMeta(MetaTitle.FavoriteGames, Collections),
              info: { needLogin: true },
            },
            {
              path: "recent-game",
              component: withMeta(MetaTitle.RecentGames, RecentGame),
              info: { needLogin: true },
            },
            {
              path: "bonus-game",
              component: withMeta(MetaTitle.BonusGame, BonusGame),
              info: { needLogin: true },
            },
            {
              path: "vip",
              component: withMeta(MetaTitle.Vip, VipPage),
            },
            {
              path: "wager-contest",
              component: withMeta(MetaTitle.WagerContest, WagerContest),
            },

            {
              path: "promotion-details",
              component: PromotionDetailsPage,
            },
            // 个人中心
            {
              path: "settings",
              component: withMeta(MetaTitle.Settings, SettingsPage, {
                noindex: true,
              }),
              info: { needLogin: true },
            },
            {
              path: "my-profile",
              component: withMeta(
                MetaTitle.MyProfile,
                MyProfile as unknown as () => JSX.Element,
                { noindex: true },
              ),
            },
            {
              path: "notify",
              component: withMeta(MetaTitle.Notify, NotifyPage, {
                noindex: true,
              }),
            },
            {
              path: "feedback",
              component: withMeta(MetaTitle.Feedback, Feedback),
            },
            {
              path: "help",
              component: withMeta(MetaTitle.Help, HelpPage),
            },
            {
              path: "responsible",
              component: withMeta(MetaTitle.Responsible, ResponsiblePage),
              info: { needLogin: true },
            },

            { ...walletRoutes() },
          ],
        },
        // 登入与未登入的 layout 不同
        {
          path: "bonus-center",
          component: ConditionalBonusCenterLayout,
          children: [
            {
              path: "/",
              component: withMeta(MetaTitle.BonusCenter, BonusCenterPage),
            },
          ],
        },
      ],
    },
    // 404 页面
    {
      path: "error",
      component: ErrorPage,
    },
    // 404 页面
    {
      path: "*",
      component: NotFoundPage,
    },
  ];
};
